import { useState, useEffect, useContext } from 'react';
import { ShoppingCart, User, Menu, X, LayoutDashboard, Utensils, Shield, Layers, MessageSquare, FileText } from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';
import { AuthContext } from '../authProvider/AuthProvider';
import logo from '../assets/Size wise logo.png';

const MenuBar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { logOutUser, isAuthenticated } = useContext(AuthContext);

    const handleLogOut = () => {
        logOutUser();
        setIsMobileMenuOpen(false);
    };

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 20) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        { name: 'System Features', href: '/system-features', icon: <Layers className="w-4 h-4 text-blue-500" /> },
        { name: 'Multi-Branch Setup', href: '/branches-setup', icon: <Utensils className="w-4 h-4 text-blue-500" /> },
        { name: 'Pricing Plans', href: '/pricing', icon: <Shield className="w-4 h-4 text-blue-500" /> },
        { name: 'Blog', href: '/blog', icon: <FileText className="w-4 h-4 text-blue-500" /> },
        { name: 'Contact Us', href: '/contact', icon: <MessageSquare className="w-4 h-4 text-blue-500" /> },
    ];

    return (
        <>
            <nav className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 border-b ${
                isScrolled 
                ? 'bg-zinc-950 border-blue-950 py-3 shadow-2xl' 
                : 'bg-zinc-900 border-zinc-800/80 py-5'
            }`}>
                <div className="w-full px-6 sm:px-12 lg:px-16 flex items-center justify-between">
                    
                    {/* --- SOKO BRANDING --- */}
                    <div className="flex items-center gap-3">
                        <Link to="/" className="flex items-center gap-3 group focus:outline-none">
                            <img src={logo} alt="SOKO Logo" className="w-9 h-9 object-contain bg-zinc-800 p-1 rounded-lg border border-zinc-700" />
                            <div className="flex flex-col">
                                <span className="text-xl font-black tracking-wider text-white uppercase font-mono">
                                    SOKO<span className="text-blue-500">.</span>
                                </span>
                                <span className="text-[9px] uppercase tracking-widest text-zinc-400 font-bold">
                                    By Top Orion
                                </span>
                            </div>
                        </Link>
                    </div>

                    {/* --- NAVIGATION LINKS --- */}
                    <div className="hidden md:flex items-center space-x-8">
                        {navLinks.map((link) => (
                            <NavLink
                                key={link.name}
                                to={link.href}
                                className={({ isActive }) => `text-sm font-bold tracking-wide transition-colors relative py-2 ${
                                    isActive 
                                    ? "text-blue-400 border-b-2 border-blue-400" 
                                    : "text-white hover:text-blue-400"
                                }`}
                            >
                                {link.name}
                            </NavLink>
                        ))}
                        
                        {isAuthenticated && (
                            <NavLink 
                                to="/admin" 
                                className={({ isActive }) => `flex items-center gap-2 px-3 py-1.5 text-sm font-bold rounded-lg border-2 ${
                                    isActive 
                                    ? "text-cyan-400 border-cyan-500 bg-zinc-950" 
                                    : "text-white border-blue-600 hover:bg-blue-600/20 transition-all"
                                }`}
                            >
                                <LayoutDashboard size={16} className="text-blue-400" />
                                <span>Management HQ</span>
                            </NavLink>
                        )}
                    </div>

                    {/* --- SYSTEM GATEWAY BUTTONS --- */}
                    <div className="hidden md:flex items-center gap-6">
                        <button className="relative p-2.5 bg-zinc-800 border border-zinc-700 hover:border-blue-500 text-white hover:text-blue-400 rounded-xl transition-colors">
                            <ShoppingCart className="w-5 h-5" />
                            <span className="absolute -top-3 -right-3 w-6 h-5 bg-blue-600 text-white text-[10px] font-black flex items-center justify-center rounded-full">
                                0
                            </span>
                        </button>

                        {isAuthenticated ? (
                            <button onClick={handleLogOut} className="px-5 py-2 text-xs font-bold text-zinc-400 border border-zinc-800 hover:border-red-500/50 hover:text-red-400 rounded-xl transition-all">
                                Disconnect
                            </button>
                        ) : (
                            <Link to={'/login'}>
                                <button className="flex items-center gap-2 px-5 py-2.5 text-sm font-black text-zinc-950 bg-blue-500 hover:bg-blue-400 rounded-xl transition-all shadow-lg shadow-blue-500/10">
                                    <User className="w-4 h-4 text-zinc-950" />
                                    <span>Request Demo</span>
                                </button>
                            </Link>
                        )}
                    </div>

                    {/* --- MOBILE TRIGGER --- */}
                    <div className="md:hidden">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="p-2.5 text-white bg-zinc-900 border border-zinc-800 rounded-xl"
                        >
                            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>

                </div>
            </nav>

            {/* --- MOBILE OVERLAY --- */}
            <div className={`fixed inset-0 z-40 bg-zinc-950 transition-all duration-300 md:hidden flex flex-col justify-between p-8 ${
                isMobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'
            }`}>
                
                <div className="flex items-center justify-between border-b border-zinc-800 pb-6 mt-20 w-full">
                    <span className="text-xs tracking-widest text-blue-500 font-black uppercase font-mono">// PLATFORM LINKS</span>
                    <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-white bg-zinc-900 border border-zinc-800 rounded-lg">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex flex-col space-y-4 w-full my-auto">
                    {navLinks.map((link) => (
                        <Link
                            key={link.name}
                            to={link.href}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="flex items-center gap-3 p-4 bg-zinc-900 border border-zinc-800 rounded-xl text-lg font-bold text-white hover:text-blue-400 hover:border-blue-500/40 transition-all"
                        >
                            {link.icon}
                            <span>{link.name}</span>
                        </Link>
                    ))}

                    {isAuthenticated && (
                        <Link
                            to="/admin"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="p-4 bg-zinc-900/40 border-2 border-blue-500 rounded-xl text-lg font-black text-blue-400 flex items-center gap-3"
                        >
                            <LayoutDashboard />
                            <span>Management HQ</span>
                        </Link>
                    )}
                </div>

                <div className="w-full flex flex-col gap-4 border-t border-zinc-800 pt-6">
                    {isAuthenticated ? (
                         <button 
                            onClick={handleLogOut}
                            className="w-full py-4 text-sm font-bold text-zinc-400 border border-zinc-800 bg-zinc-900 rounded-xl"
                        >
                            Disconnect Session
                        </button>
                    ) : (
                        <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="w-full">
                            <button className="w-full py-4 text-sm font-black text-zinc-950 bg-blue-500 rounded-xl text-center shadow-xl">
                                Request Enterprise Demo
                            </button>
                        </Link>
                    )}
                </div>
            </div>
        </>
    );
};

export default MenuBar;